import React from "react";

// ── Flavor Radar — auto-computed 6-axis sensory fingerprint (spider chart) ──
// Values come from the API (computed live from the formula), 0–5 per axis.
const RADAR_AXES = [
  { key: "aroma",     label: "Aroma" },
  { key: "umami",     label: "Umami" },
  { key: "heat",      label: "Heat" },
  { key: "mouthfeel", label: "Mouthfeel" },
  { key: "acidity",   label: "Acidity" },
  { key: "sweetness", label: "Sweetness" },
];

export default function FlavorRadar({ radar, size = 150 }) {
  if (!radar) return null;
  const vals = RADAR_AXES.map((a) => Math.max(0, Math.min(5, Number(radar[a.key]) || 0)));
  if (!vals.some((v) => v > 0)) return null;
  const cx = size / 2, cy = size / 2;
  const R = size / 2 - 26; // leave room for labels
  const pt = (i, r) => {
    const ang = (Math.PI * 2 * i) / 6 - Math.PI / 2; // start at top
    return [cx + r * Math.cos(ang), cy + r * Math.sin(ang)];
  };
  const ring = (r) => RADAR_AXES.map((_, i) => pt(i, r).join(",")).join(" ");
  const shape = vals.map((v, i) => pt(i, (v / 5) * R).join(",")).join(" ");
  return (
    <div className="mt-3 pt-3 border-t border-stone-100" data-testid="flavor-radar">
      <div className="text-[10px] uppercase tracking-wider font-bold text-stone-400 mb-1">
        Flavor Radar <span className="normal-case font-normal">· auto from formula</span>
      </div>
      <div className="flex items-center gap-3">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0">
          {[1, 2, 3, 4, 5].map((s) => (
            <polygon key={s} points={ring((s / 5) * R)} fill="none"
              stroke="#e7e5e4" strokeWidth={s === 5 ? 1.2 : 0.7} />
          ))}
          {RADAR_AXES.map((_, i) => {
            const [x, y] = pt(i, R);
            return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="#e7e5e4" strokeWidth="0.7" />;
          })}
          <polygon points={shape} fill="rgba(5,122,85,0.18)" stroke="#057a55" strokeWidth="1.5" strokeLinejoin="round" />
          {vals.map((v, i) => {
            const [x, y] = pt(i, (v / 5) * R);
            return <circle key={i} cx={x} cy={y} r="2.2" fill="#057a55" />;
          })}
          {RADAR_AXES.map((a, i) => {
            const [x, y] = pt(i, R + 13);
            return (
              <text key={a.key} x={x} y={y} textAnchor="middle" dominantBaseline="middle"
                fontSize="8.5" fill="#78716c">{a.label}</text>
            );
          })}
        </svg>
        <div className="grid grid-cols-1 gap-0.5 text-[11px] text-stone-500">
          {RADAR_AXES.map((a, i) => (
            <div key={a.key} className="flex justify-between gap-2 w-24">
              <span>{a.label}</span>
              <span className="font-mono text-stone-700">{vals[i].toFixed(1)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
